import { serverUrl } from "../common/contants";
import { getUid } from "../auth/AuthManager";
import { SortType } from "../settings/model/SortType";
import { convertSortTypeString } from "./terms";
import { TermLevel } from "./custom_view/TermLevel";
import Term from "./Term";
import OnevocaServerError from "../common/OnevocaServerError";
import getApiInstance from "../common/api";
import Group from "../groups/Group";
import { TermListUploadResult } from "../csv/model/CSVTermListUploadResult";
import { CSVTermListUploadMode } from "../csv/model/CSVTermListUploadMode";

export async function fetchTermFromDictionary(text: string): Promise<string[]> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/word/getTermFromDictionary.php?uid=${uid}&text=${text}`
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      if (response.data.data === undefined) {
        return [];
      }
      const definitionsData = response.data.data as {
        definition: string;
        normalized: string;
      }[];
      const definitionsStringArray = definitionsData.map(
        (item) => item.definition
      );
      return definitionsStringArray;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchAutoPronunciation(
  text: string,
  language: string
): Promise<string> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      "/pronunciation/getPronunciation.php",
      { params: { uid: uid, requestWord: text, requestLanguage: language } }
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return response.data.result ?? "";
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchTerms(
  seed: number,
  sort?: SortType
): Promise<Term[]> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      seed: seed,
      sort: sort === undefined ? null : convertSortTypeString(sort),
    };
    const response = await getApiInstance(undefined).post(
      `/word/get_word_list_v4.php`,
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      if (response.data.data === undefined) {
        return [];
      }
      const termsData = response.data.data;
      const terms = termsData.map((termData: any) => Term.fromJson(termData));
      return terms;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateTermMark(term: Term, isOn: boolean): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      termId: term.id,
      uid: uid,
      isOn: isOn,
    };

    const response = await getApiInstance(undefined).post(
      `/word/toggleMarked.php`,
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateTermLevel(
  term: Term,
  toLevel: TermLevel
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      id: term.id,
      uid: uid,
      newLevel: toLevel,
      after108v: true,
    };

    const response = await getApiInstance(undefined).post(
      `/word/edit_level.php`,
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateTerm(term: Term): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      id: term.id,
      uid: uid,
      n_word: term.term,
      n_mean: term.definition,
      n_desc: term.memo,
      n_pron: term.pronunciation,
      n_group: term.group,
    };

    const response = await getApiInstance(undefined).post(
      `/word/edit.php`,
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateTermInformation(
  termID: string,
  term?: string,
  definition?: string,
  memo?: string,
  pronunciation?: string
) {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      term_id: termID,
      term: term,
      definition: definition,
      memo: memo,
      pronunciation: pronunciation,
    };

    const response = await getApiInstance(undefined).post(
      `/word/updateTermInformation.php`,
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function deleteTerms(toTerms: Term[]): Promise<void> {
  const parameters: { [key: string]: any } = {};
  toTerms.forEach((term, i) => {
    parameters[`id${i}`] = term.id;
  });

  try {
    const uid = await getUid();
    parameters["uid"] = uid;
    const response = await getApiInstance(undefined).post(
      "/word/remove_selected.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateTermsLevel(
  toTerms: Term[],
  toLevel: TermLevel
): Promise<void> {
  const parameters: { [key: string]: any } = {};
  parameters["newLevel"] = toLevel;
  parameters["after108v"] = true;
  toTerms.forEach((term, i) => {
    parameters[`id${i}`] = term.id;
  });

  try {
    const uid = await getUid();
    parameters["uid"] = uid;
    const response = await getApiInstance(undefined).post(
      "/word/edit_level_selected.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateTermsGroup(
  toTerms: Term[],
  toGroup: Group
): Promise<void> {
  const newGroup = toGroup.isUnspecifiedGroup ? "" : toGroup.title;
  const parameters: { [key: string]: any } = {};
  parameters["ngn"] = newGroup;
  toTerms.forEach((term, i) => {
    parameters[`id${i}`] = term.id;
  });

  try {
    const uid = await getUid();
    parameters["uid"] = uid;
    const response = await getApiInstance(undefined).post(
      "/word/edit_group_selected.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function deleteTerm(term: Term): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      id: term.id,
    };
    const response = await getApiInstance(undefined).post(
      `${serverUrl}/word/remove.php`,
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

function makeInsertTermsParameters(terms: Term[]): { [key: string]: any } {
  const termParameters: { [key: string]: any } = {};
  terms.forEach((term, i) => {
    termParameters[`term${i}`] = term.term;
    termParameters[`defi${i}`] =
      term.definition.length !== 0 ? term.definition : term.defaultHints ?? "";
    termParameters[`memo${i}`] = term.memo;
    termParameters[`pron${i}`] = term.pronunciation;
  });
  return termParameters;
}

// 중복 확인을 켜고 같은 단어가 이미 있으면 저장하지 않고 그 단어들을 돌려준다
export async function insertTerm(
  term: Term,
  toGroup: string,
  isCheckDuplicated: boolean
): Promise<Term[]> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      is_checked_duplicate: isCheckDuplicated,
      wd: term.term,
      mn:
        term.definition.length !== 0 ? term.definition : term.defaultHints ?? "",
      de: term.memo,
      pr: term.pronunciation,
      gr: toGroup,
    };
    const response = await getApiInstance(undefined).post(
      "/word/insertTerm.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      const duplicatedTermsData = response.data.duplicate_words;
      if (duplicatedTermsData === undefined) {
        return [];
      }
      return duplicatedTermsData.map((termData: any) =>
        Term.fromJson(termData)
      );
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function getTermsCount(): Promise<number> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/word/getTermsCount.php?uid=${uid}`
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const count = response.data.count;
    if (count !== undefined && typeof count === "number") {
      return count;
    } else {
      return Promise.reject(new Error("?"));
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function uploadTermList(
  terms: Term[],
  group: string,
  mode: CSVTermListUploadMode
): Promise<TermListUploadResult> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      mode: mode,
      group: group,
      terms: makeInsertTermsParameters(terms),
    };

    const response = await getApiInstance(undefined).post(
      "/word/insertTermList_v2.php",
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const result = response.data.result;
    if (result === undefined) {
      return Promise.reject("Found not result.");
    }

    return TermListUploadResult.fromJSON(result);
  } catch (error) {
    return Promise.reject(error);
  }
}
